import { Dispatch, FC, SetStateAction } from "react";
import { Checkbox, FormControlLabel, SxProps, Typography, Theme } from "@mui/material";
import CaseHitsLensFilter from "./CaseHitsLensFilter";
import { parseISO } from "date-fns";
import Flex from "components/Flex";
import Caption from "components/Caption";
import SectionSelect from "components/SectionSelect";
import PCheckbox from "components/PCheckbox";
import { useAppSelector } from "redux/hooks";
import { LensHitFilterState } from "./LensHitFilterState";
import {
  selectReviewCurrentCancerRecurrenceYesCount,
  selectReviewCurrentFindings,
  selectSectionOptions,
} from "redux/slices/case";

import { CaseIntakesMultipleAutocomplete } from "components/CaseIntakesAutocomplete";
import { CustomDocumentProperty, KeyWithValues, ReviewConfig } from "generated/graphql";
import { selectCaseInfo } from "redux/slices/case";
import { contains } from "helpers/coll";
import PDatePicker from "components/PDatePicker";
import CustomPropertyCell from "components/CustomPropertyCell";
import { selectDocumentProperties } from "redux/slices/clientLens";
import theme from "theme";

interface CancerRecurrenceCheckboxProps {
  filterState: LensHitFilterState;
  sx?: SxProps<Theme>;
}

export const CancerRecurrenceCheckbox = ({ filterState, sx }: CancerRecurrenceCheckboxProps) => {
  const cancerRecurrenceYesCount = useAppSelector(selectReviewCurrentCancerRecurrenceYesCount);
  const label = `Recurring (${cancerRecurrenceYesCount})`;
  return (
    <FormControlLabel
      color="primary"
      label={label}
      sx={sx}
      title={label}
      control={
        <Checkbox
          checked={filterState.showCancerRecurrence}
          onChange={(evt) => filterState.setShowCancerRecurrence(evt.target.checked)}
          size="small"
          color="secondary"
        />
      }
    />
  );
};

const DocumentPropertyFilter: FC<{
  cp: KeyWithValues;
  documentProperties: CustomDocumentProperty[];
  stateProperties: KeyWithValues[];
  setStateProperties: Dispatch<SetStateAction<KeyWithValues[]>>;
}> = ({ documentProperties, cp, stateProperties, setStateProperties }) => {
  const customProperty = documentProperties.find((prop) => prop.key === cp.key);
  const dp = stateProperties.find((dp) => dp.key === cp.key);

  const handleChange = (v: string | undefined) => {
    setStateProperties((prev) => {
      const foundIndex = prev.findIndex((p) => p.key === cp.key);
      const newPrev = prev.slice();

      if (foundIndex !== -1) {
        if (v !== undefined) {
          newPrev.splice(foundIndex, 1, {
            key: cp.key,
            values: [v as string],
          });
        } else {
          newPrev.splice(foundIndex, 1);
        }
      } else {
        newPrev.push({ key: cp.key, values: [v as string] });
      }

      return newPrev;
    });
  };
  return customProperty ? (
    <CustomPropertyCell
      key={cp.key}
      value={dp?.values[0]}
      customProperty={customProperty}
      setValue={(v) => handleChange(v as string | undefined)}
    />
  ) : (
    <></>
  );
};

export interface LensHitFilterControlsProps {
  lensId: number;
  caseId: number;
  showRules: boolean;
  showLensCounts: boolean;
  filterState: LensHitFilterState;
  reviewConfig?: ReviewConfig;
}

const LensHitFilterControls = (props: LensHitFilterControlsProps) => {
  const { caseId, filterState, reviewConfig } = props;
  const documentProperties = useAppSelector(selectDocumentProperties);

  const caseInfo = useAppSelector(selectCaseInfo);
  const showRequiredDocumentPropertiesFilter = !!reviewConfig?.requiredCustomProperties.length;
  const showExcludedDocumentPropertiesFilter = !!reviewConfig?.excludedCustomProperties.length;

  const currentFindings = useAppSelector(selectReviewCurrentFindings)?.findings || [];
  const selectedFindings = currentFindings.filter((f) => contains(filterState.selectedRuleIds, f.categoryId));

  const sectionOptions = useAppSelector(selectSectionOptions);

  const dateOfBirth = caseInfo.dateOfBirth ? parseISO(caseInfo.dateOfBirth) : undefined;
  const dateOfDeath = caseInfo.dateOfDeath ? parseISO(caseInfo.dateOfDeath) : undefined;

  const showProductInjuryOtherRuleSelects = props.showRules && !filterState.currentField;
  const showSmartFilters = !filterState.currentFilters && props.showRules;

  return (
    <Flex flexDirection="column" flexShrink={0}>
      <Caption bold text="Filter by Section" />
      {showProductInjuryOtherRuleSelects && (
        <>
          <Flex p={1}>
            <CaseHitsLensFilter
              label="Finding Category"
              sx={{
                flexGrow: 1,
                marginRight: theme.spacing(1),
              }}
              groupBy={(finding) => finding.ruleType.subtype}
              options={currentFindings}
              value={selectedFindings}
              onChange={(ruleIds) => filterState.setSelectedRuleIds(ruleIds)}
              showCounts={props.showLensCounts}
            />
          </Flex>
        </>
      )}

      <Flex p={1}>
        <SectionSelect
          sectionOptions={sectionOptions}
          pageTypes={filterState.selectedPageTypes}
          setPageTypes={(pageTypes) => filterState.setSelectedPageTypes(pageTypes || [])}
          clusterTypes={filterState.selectedClusterTypes}
          setClusterTypes={(clusterTypes) => filterState.setSelectedClusterTypes(clusterTypes || [])}
          pageLabels={filterState.selectedPageLabels}
          setPageLabels={(pageLabels) => filterState.setSelectedPageLabels(pageLabels || [])}
        />
      </Flex>

      <Caption bold text="Filter by Date" />
      <Flex>
        <Flex flexDirection="row" p={1}>
          <PDatePicker
            label="Start"
            format="MM/dd/yyyy"
            date={filterState.selectedStartDate}
            setDate={filterState.setSelectedStartDate}
            minDate={dateOfBirth}
            maxDate={dateOfDeath}
          />
          <Flex alignItems="center" justifyContent="center" margin="0px 10px">
            <Typography variant="h6">-</Typography>
          </Flex>
          <PDatePicker
            label="End"
            format="MM/dd/yyyy"
            date={filterState.selectedEndDate}
            setDate={filterState.setSelectedEndDate}
            minDate={dateOfBirth}
            maxDate={dateOfDeath}
          />
        </Flex>
      </Flex>
      <Caption bold text="Filter by Document" />
      <Flex p={1}>
        <CaseIntakesMultipleAutocomplete
          caseId={caseId}
          label="Documents"
          selected={filterState.selectedIntakes}
          onChange={filterState.setSelectedIntakes}
        />
      </Flex>
      {showRequiredDocumentPropertiesFilter && (
        <>
          <Caption text="Required Document Properties" bold />
          <Flex p={1} sx={{ gap: 2 }}>
            {reviewConfig.requiredCustomProperties.map((cp) => (
              <DocumentPropertyFilter
                key={cp.key}
                cp={cp}
                stateProperties={filterState.documentRequiredProperties}
                setStateProperties={filterState.setDocumentRequiredProperties}
                documentProperties={documentProperties}
              />
            ))}
          </Flex>
        </>
      )}
      {showExcludedDocumentPropertiesFilter && (
        <>
          <Caption text="Excluded Document Properties" bold />
          <Flex p={1} sx={{ gap: 2 }}>
            {reviewConfig.excludedCustomProperties.map((cp) => (
              <DocumentPropertyFilter
                key={cp.key}
                cp={cp}
                stateProperties={filterState.documentExcludedProperties}
                setStateProperties={filterState.setDocumentExcludedProperties}
                documentProperties={documentProperties}
              />
            ))}
          </Flex>
        </>
      )}

      <Caption bold text="Smart Filters" />
      <Flex p={1} flexDirection="row">
        {showSmartFilters && (
          <>
            <PCheckbox
              label="Hide Negated Findings"
              checked={!filterState.showNegated}
              onChange={(_evt, checked) => filterState.setShowNegated(!checked)}
            />
            <PCheckbox
              label="Hide Repetitive Findings"
              checked={filterState.hideDuplicates}
              onChange={(_evt, checked) => filterState.setHideDuplicates(checked)}
            />
          </>
        )}
        <PCheckbox
          label="Claimant"
          checked={filterState.claimantHitsAllowed}
          onChange={(evt) => {
            filterState.toggleClaimantAllowed(evt.target.checked);
          }}
        />
        <PCheckbox
          label="Family"
          checked={filterState.familyHitsAllowed}
          onChange={(evt) => filterState.toggleFamilyAllowed(evt.target.checked)}
        />
      </Flex>
      <Caption bold text="Exclude Sections" />
      <Flex p={1}>
        <SectionSelect
          overrideLabel="Exclude Sections"
          sectionOptions={sectionOptions}
          pageTypes={filterState.selectedNotPageTypes}
          setPageTypes={(pageTypes) => filterState.setSelectedNotPageTypes(pageTypes)}
          clusterTypes={filterState.selectedNotClusterTypes}
          setClusterTypes={(clusterTypes) => filterState.setSelectedNotClusterTypes(clusterTypes)}
          pageLabels={filterState.selectedNotPageLabels}
          setPageLabels={(pageLabels) => filterState.setSelectedNotPageLabels(pageLabels)}
        />
      </Flex>

      <Caption bold text="Sort categories" />
      <Flex p={1} flexDirection="row">
        <PCheckbox
          label="Chronological (default)"
          checked={filterState.sortFindings === "default"}
          onChange={(evt) => {
            if (evt.target.checked) {
              filterState.setSortFindings("default");
            } else {
              filterState.setSortFindings("alpha");
            }
          }}
        />
        <PCheckbox
          label="Alphabetical"
          checked={filterState.sortFindings === "alpha"}
          onChange={(evt) => {
            if (evt.target.checked) {
              filterState.setSortFindings("alpha");
            } else {
              filterState.setSortFindings("default");
            }
          }}
        />
      </Flex>
    </Flex>
  );
};

export default LensHitFilterControls;
